import React, { useEffect, useState } from 'react';
import { Button, Typography, Spin, theme } from 'antd';
import { IconArrowLeft, IconCalendarOff, IconMessageCircle, IconScale } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import AppLayout from '../../components/Layouts/Layout';
import PageColumn from '../../components/Layouts/PageColumn';
import EmptyState from '../../components/EmptyState';
import BookingTimeline from '../../components/BookingTimeline';
import BookingReferenceTag from '../../components/BookingReferenceTag';
import BookingMoneyCard from '../../components/BookingMoneyCard';
import BookingGeofenceCard from '../../components/BookingGeofenceCard';
import BookingLifecycleActions from '../../components/BookingLifecycleActions';
import NearbyOperatorsSection from '../../components/NearbyOperatorsSection';
import { getBookingById } from '../../services/BookingService';
import { getCheckInOutReports } from '../../services/CheckInOutService';
import { ICheckInOutReport } from '../../interfaces/heavyDutyMachinery/ICheckInOut';
import { useAuth } from '../../contextProviders/AuthProvider';
import { IBooking } from '../../interfaces/heavyDutyMachinery/IBooking';
import { BOOKING_STATUS_LABEL_KEYS, BOOKING_STATUS_TONES, CHAT_ENABLED_STATUSES, NEGATIVE_BOOKING_STATUSES } from '../../constants/bookingStatusOptions';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import { formatShortDateTime } from '../../helpers/dateFormatHelper';
import { pillToneStyle } from '../../theme/creamyTokens';

const { Title, Text } = Typography;

/**
 * Per-booking "seguimiento" screen -- the same view for both sides of a
 * rental (the backend's own GetBooking already authorizes either the guest
 * or the space's host, and nothing here is role-specific yet), built
 * mobile-first at the same 480px column every other per-booking flow uses
 * (see CheckInOutFlow).
 *
 * Deliberately read-only for now: it answers "where is my machine and where
 * is my money", which is the question both parties open it for. The actions
 * that advance a phase land next to their own phase in a later pass, so the
 * shape of this page doesn't have to change to accommodate them.
 */
const BookingTimelinePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { userId } = useAuth();
  const [booking, setBooking] = useState<IBooking | undefined>(undefined);
  const [reports, setReports] = useState<ICheckInOutReport[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    // The reports are fetched alongside the booking rather than on demand
    // because whether there is an action to offer at all depends on them
    // (see BookingLifecycleActions) -- a card that appears a beat after the
    // page settles reads as a glitch. A booking with no Machinery has no
    // reports endpoint to speak of, so that failure is absorbed into an
    // empty list rather than failing the whole page.
    Promise.all([getBookingById(id), getCheckInOutReports(id).catch(() => [] as ICheckInOutReport[])])
      .then(([loadedBooking, loadedReports]) => {
        if (cancelled) return;
        setBooking(loadedBooking);
        setReports(loadedReports);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const backButton = (
    <Button
      type="text"
      size="small"
      icon={<IconArrowLeft size={16} />}
      style={{ marginLeft: -8, marginBottom: 8 }}
      onClick={() => navigate(-1)}
    >
      {translate(t, TRANSLATIONS.BOOKING_TIMELINE_BACK_BUTTON)}
    </Button>
  );

  if (loading) {
    return (
      <AppLayout>
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      </AppLayout>
    );
  }

  if (!booking) {
    return (
      <AppLayout>
        <PageColumn>
          {backButton}
          <EmptyState
            icon={<IconCalendarOff size={24} />}
            title={translate(t, TRANSLATIONS.BOOKING_TIMELINE_NOT_LOADED)}
            description={translate(t, TRANSLATIONS.BOOKING_TIMELINE_NOT_LOADED_DESCRIPTION)}
          />
        </PageColumn>
      </AppLayout>
    );
  }

  // The page already derives "is host" inline for BookingLifecycleActions;
  // named once here because the operator section below needs its opposite
  // and a second inline negation of the same expression is how the two
  // drift apart later.
  const isGuestViewing = !!userId && booking.guestUserId === userId;

  return (
    <AppLayout>
      <PageColumn>
        {backButton}
        <Title level={4} style={{ margin: '0 0 2px' }}>
          {booking.heavyDutyMachineryTitle}
        </Title>
        <BookingReferenceTag reference={booking.reference} copyable style={{ display: 'block', marginBottom: 6 }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
          <span
            style={{
              display: 'inline-flex',
              padding: '3px 10px',
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 600,
              ...pillToneStyle(BOOKING_STATUS_TONES[booking.status], token),
            }}
          >
            {translate(t, BOOKING_STATUS_LABEL_KEYS[booking.status])}
          </span>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {formatShortDateTime(booking.startAt, i18n.language)} – {formatShortDateTime(booking.endAt, i18n.language)}
          </Text>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Above the timeline on purpose: someone opening this page with
              something to do should not have to read nine phases to find
              out what it is. */}
          <BookingLifecycleActions
            booking={booking}
            reports={reports}
            isHost={!!userId && booking.guestUserId !== userId}
            userId={userId ?? undefined}
            onBookingChanged={setBooking}
          />
          <BookingTimeline booking={booking} />
          {/* Between the timeline and the money: "where is my machine" is the
              other half of the question this page exists to answer, and on a
              rental in progress it is the half people open it for. Only for a
              Machinery booking -- geofencing has nothing to say about a
              service. */}
          {!!booking.heavyDutyMachineryId && (
            <BookingGeofenceCard booking={booking} isHost={!!userId && booking.guestUserId !== userId} />
          )}
          <BookingMoneyCard booking={booking} />
          {/* "You have the machine but nobody to run it." This is the moment
              that actually matters: somebody who has already paid for a
              rental is far more aware they need an operator than somebody
              still filling in dates, and unlike at checkout the rental is
              secured here, so these rows can be hired for real.

              Only for the guest (the host has their own staff and does not
              hire from here), only on a live Machinery booking that has not
              ended, and only while the booking has nobody on it yet --
              neither one of the host's own operators nor a Service line.
              The section itself renders nothing when the host does not allow
              outside operators, so a host's "no" is respected here exactly
              as it is at checkout. */}
          {isGuestViewing &&
            !!booking.heavyDutyMachineryId &&
            !booking.operatorId &&
            booking.serviceLines.length === 0 &&
            !NEGATIVE_BOOKING_STATUSES.includes(booking.status) &&
            new Date(booking.endAt).getTime() > Date.now() && (
              <NearbyOperatorsSection
                spaceId={String(booking.heavyDutyMachineryId)}
                lat={booking.deliveryLatitude}
                lng={booking.deliveryLongitude}
                startAt={booking.startAt}
                endAt={booking.endAt}
                mode="hire"
                // Straight to that operator's own booking flow, with the
                // rental's dates carried over so the guest does not retype
                // what this page already knows. A separate booking with its
                // own payment, which is the entire point -- see
                // INearbyOperator's own doc comment.
                onHire={(operator) =>
                  navigate(`/heavy-duty-machinery/services/${operator.id}?startAt=${encodeURIComponent(booking.startAt)}`)
                }
              />
            )}
          {/* Every question this screen raises ("why is it still in
              transport?", "what was that extra charge?") is answered by the
              other party, so the way out of here is the conversation -- same
              gate the backend puts on sending a message (see
              ConversationsController.SendMessage). */}
          {/* Only once the machine has actually changed hands: before that
              there is nothing to have gone wrong with, and a cancel is the
              right lever rather than a claim. */}
          {!!booking.handedOverAt && (
            <Button
              shape="round"
              block
              size="large"
              icon={<IconScale size={18} />}
              onClick={() => navigate(`/heavy-duty-machinery/bookings/${booking.id}/dispute`)}
            >
              {translate(t, TRANSLATIONS.DISPUTE_OPEN_FROM_BOOKING)}
            </Button>
          )}
          {CHAT_ENABLED_STATUSES.includes(booking.status) && (
            <Button
              shape="round"
              block
              size="large"
              icon={<IconMessageCircle size={18} />}
              onClick={() => navigate(`/heavy-duty-machinery/messages?bookingId=${booking.id}`)}
            >
              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_CHAT_BUTTON)}
            </Button>
          )}
        </div>
      </PageColumn>
    </AppLayout>
  );
};

export default BookingTimelinePage;
